import { useTranslation } from 'react-i18next';
import { UI_LANGUAGES } from '@manpower/shared';
import { useAuth } from '@/auth/AuthProvider';
import { api } from '@/lib/api';
import { changeLanguage } from '../i18n';

/**
 * Language picker.
 *
 * Every option is labelled in its own script, never "Tamil" or "Hindi" in
 * English: someone who cannot read English cannot find their language in an
 * English list, and those are exactly the people this control exists for.
 */
export function LanguageSwitcher({ className = '' }: { className?: string }) {
  const { i18n, t } = useTranslation();
  const { user } = useAuth();

  const onChange = async (code: string) => {
    // Switch first: the interface must respond immediately, whether or not the
    // request below succeeds.
    await changeLanguage(code);

    // Then remember it on the account. localStorage alone would strand the
    // choice on one browser, and the people this matters to change handsets,
    // share them and reinstall - re-finding this control on a new phone means
    // navigating a UI they cannot read.
    if (!user) return;
    try {
      await api.patch('/auth/me/language', { language: code });
    } catch {
      // The language has already changed locally. Failing to persist it is not
      // worth an error message to someone who just wanted to read the page.
    }
  };

  return (
    <label className={`inline-flex items-center gap-2 ${className}`}>
      <span className="sr-only">{t('language.choose', 'Choose language')}</span>
      <select
        value={i18n.language}
        onChange={(event) => void onChange(event.target.value)}
        className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        aria-label={t('language.choose', 'Choose language')}
      >
        {UI_LANGUAGES.map((language) => (
          // lang on the option so a screen reader switches voice rather than
          // reading Tamil script with an English pronunciation.
          <option key={language.code} value={language.code} lang={language.code}>
            {language.label}
          </option>
        ))}
      </select>
    </label>
  );
}
